import { useState, useEffect, FC } from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { Input } from '../../ui/Input'
import { Select } from '../../ui/Select'
import { Button } from '../../ui/Button'
import { FileUpload } from '../../ui/FileUpload'
import majorityShareholderService from '../../../services/majorityShareholderService'
import postalCodeService from '../../../services/postalCodeService'
import { authService } from '../../../services/authService'
import toast from 'react-hot-toast'
import apiClient from '../../../services/api'
import ContinueLaterModal from '../ContinueLaterModal'
import type { StepData } from '../../../services/onboardingService'

interface ShareholderFormValues {
  firstName: string
  lastName: string
  motherLastName: string
  rfc: string
  birthDate: string
  nationality: string
  phoneNumber: string
  email: string
  street: string
  exteriorNumber: string
  interiorNumber: string
  betweenStreets: string
  postalCode: string
  colony: string
  city: string
  municipality: string
  state: string
  stateAbbreviation: string
  ineFront: File | null
  ineBack: File | null
}

interface ShareholderDataStepProps {
  onComplete?: (stepData: StepData) => Promise<void>
  stepData?: any
  stepKey?: string
}

// Función para extraer fecha de nacimiento del RFC
const extractBirthDateFromRFC = (rfc: string): string | null => {
  if (!rfc || rfc.length < 10) return null

  try {
    // Los primeros 4 caracteres son las iniciales
    // Los siguientes 6 son la fecha: YYMMDD
    const dateStr = rfc.substring(4, 10)

    if (!/^\d{6}$/.test(dateStr)) return null

    const year = parseInt(dateStr.substring(0, 2))
    const month = parseInt(dateStr.substring(2, 4))
    const day = parseInt(dateStr.substring(4, 6))

    // Validar mes y día
    if (month < 1 || month > 12 || day < 1 || day > 31) return null

    // Determinar el siglo (RFC usa YY, asumimos que años 00-29 son 2000-2029, 30-99 son 1930-1999)
    const fullYear = year <= 29 ? 2000 + year : 1900 + year

    // Crear fecha y validar que sea válida
    const date = new Date(fullYear, month - 1, day)
    if (
      date.getFullYear() !== fullYear ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null
    }

    // Formato YYYY-MM-DD para input type="date"
    return `${fullYear}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`
  } catch (error) {
    console.error('Error extracting birth date from RFC:', error)
    return null
  }
}

// Función para validar mayoría de edad
const validateMinimumAge = (
  birthDate: string,
  minimumAge: number = 21 // Cambiado de 18 a 21
): boolean => {
  if (!birthDate) return false

  try {
    const birth = new Date(birthDate)
    const today = new Date()

    let age = today.getFullYear() - birth.getFullYear()
    const monthDiff = today.getMonth() - birth.getMonth()

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      age--
    }

    return age >= minimumAge
  } catch (error) {
    console.log(error)
    return false
  }
}

const validationSchema = Yup.object({
  firstName: Yup.string()
    .trim()
    .min(1, 'El nombre no puede estar vacío')
    .required('El nombre es requerido'),
  lastName: Yup.string()
    .trim()
    .min(1, 'El apellido paterno no puede estar vacío')
    .required('El apellido paterno es requerido'),
  motherLastName: Yup.string()
    .trim()
    .min(1, 'El apellido materno no puede estar vacío')
    .required('El apellido materno es requerido'),
  rfc: Yup.string()
    .transform(value => (value ? value.toUpperCase().trim() : value))
    .min(1, 'El RFC no puede estar vacío')
    .matches(
      /^([A-ZÑ\x26]{4}([0-9]{2})(0[1-9]|1[0-2])(0[1-9]|1[0-9]|2[0-9]|3[0-1]))([A-Z\d]{3})$/,
      'El RFC debe tener el formato válido para persona física (4 letras, 6 números, 3 caracteres) y con fecha válida'
    )
    .required('El RFC es requerido'),
  birthDate: Yup.string()
    .trim()
    .min(1, 'La fecha de nacimiento no puede estar vacía')
    .required('La fecha de nacimiento es requerida')
    .test(
      'minimum-age',
      'La persona debe ser mayor de 21 años', // Cambiado de "mayor de edad (18 años)" a "mayor de 21 años"
      function (value) {
        return validateMinimumAge(value || '', 21) // Cambiado de 18 a 21
      }
    )
    .test(
      'rfc-match',
      'La fecha de nacimiento debe coincidir con el RFC',
      function (value) {
        const { rfc } = this.parent
        if (!rfc || !value) return true

        const extractedDate = extractBirthDateFromRFC(rfc)
        if (!extractedDate) return true // Si no se puede extraer, no validamos coincidencia

        return extractedDate === value
      }
    ),
  nationality: Yup.string()
    .trim()
    .min(1, 'La nacionalidad no puede estar vacía')
    .required('La nacionalidad es requerida'),
  phoneNumber: Yup.string()
    .trim()
    .min(1, 'El teléfono no puede estar vacío')
    .matches(/^\d{10}$/, 'El teléfono debe tener exactamente 10 dígitos')
    .required('El teléfono es requerido'),
  email: Yup.string()
    .trim()
    .min(1, 'El correo electrónico no puede estar vacío')
    .email('El formato del correo electrónico no es válido')
    .required('El correo electrónico es requerido'),
  street: Yup.string()
    .trim()
    .min(1, 'La calle no puede estar vacía')
    .required('La calle es requerida'),
  exteriorNumber: Yup.string()
    .trim()
    .min(1, 'El número exterior no puede estar vacío')
    .required('El número exterior es requerido'),
  interiorNumber: Yup.string().test(
    'not-empty-if-filled',
    'El número interior no puede estar vacío',
    function (value) {
      // Si el valor original (sin trim) está vacío, está bien
      if (!value) return true
      // Si tiene contenido, verificar que no sea solo espacios
      return value.trim().length > 0
    }
  ),
  betweenStreets: Yup.string().test(
    'not-empty-if-filled',
    'Entre calles no puede estar vacío',
    function (value) {
      // Si el valor original (sin trim) está vacío, está bien
      if (!value) return true
      // Si tiene contenido, verificar que no sea solo espacios
      return value.trim().length > 0
    }
  ),
  postalCode: Yup.string()
    .trim()
    .min(1, 'El código postal no puede estar vacío')
    .matches(/^\d{5}$/, 'El código postal debe tener 5 dígitos')
    .required('El código postal es requerido'),
  colony: Yup.string()
    .trim()
    .min(1, 'La colonia no puede estar vacía')
    .required('La colonia es requerida'),
  city: Yup.string()
    .trim()
    .min(1, 'La ciudad no puede estar vacía')
    .required('La ciudad es requerida'),
  state: Yup.string()
    .trim()
    .min(1, 'El estado no puede estar vacío')
    .required('El estado es requerido'),
})

const handlePhoneChange = (value: string, setFieldValue: any) => {
  const digitsOnly = value.replace(/\D/g, '').slice(0, 10)
  setFieldValue('phoneNumber', digitsOnly)
}

const handleNameChange = (
  value: string,
  fieldName: string,
  setFieldValue: any
) => {
  // Only allow letters, spaces, and common name characters (ñ, accents)
  const lettersOnly = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '')
  setFieldValue(fieldName, lettersOnly)
}

const ShareholderDataStep: FC<ShareholderDataStepProps> = ({ onComplete }) => {
  const [isLoadingData, setIsLoadingData] = useState(true)
  const [postalCodeData, setPostalCodeData] = useState<any>(null)
  const [colonies, setColonies] = useState<
    Array<{ value: string; label: string }>
  >([])
  const [previews, setPreviews] = useState({
    ineFront: null as string | null,
    ineBack: null as string | null,
  })

  const [initialValues, setInitialValues] = useState<ShareholderFormValues>({
    firstName: '',
    lastName: '',
    motherLastName: '',
    rfc: '',
    birthDate: '',
    nationality: '',
    phoneNumber: '',
    email: '',
    street: '',
    exteriorNumber: '',
    interiorNumber: '',
    betweenStreets: '',
    postalCode: '',
    colony: '',
    city: '',
    municipality: '',
    state: '',
    stateAbbreviation: '',
    ineFront: null,
    ineBack: null,
  })

  const nationalityOptions = [
    { value: 'mexico', label: 'México' },
    { value: 'estados-unidos', label: 'Estados Unidos' },
    { value: 'canada', label: 'Canadá' },
    { value: 'espana', label: 'España' },
    { value: 'otra', label: 'Otra' },
  ]

  // Handle RFC change and auto-extract birth date
  const handleRfcChange = (value: string, setFieldValue: any) => {
    const upperValue = value.toUpperCase()
    setFieldValue('rfc', upperValue)

    // Auto-extract birth date if RFC is complete
    if (upperValue.length >= 10) {
      const extractedDate = extractBirthDateFromRFC(upperValue)
      if (extractedDate) {
        setFieldValue('birthDate', extractedDate)

        // Validate minimum age and show message
        if (!validateMinimumAge(extractedDate, 21)) {
          // Cambiado de 18 a 21
          toast.error('La persona debe ser mayor de 21 años') // Cambiado de "mayor de edad (18 años)" a "mayor de 21 años"
        } else {
          toast.success('Fecha de nacimiento extraída del RFC')
        }
      }
    }
  }

  // Load existing shareholder data on component mount
  useEffect(() => {
    const loadExistingData = async () => {
      try {
        setIsLoadingData(true)
        console.log('Loading existing shareholder data...')

        const existingShareholders =
          await majorityShareholderService.getShareholders()
        console.log('API Response:', existingShareholders)

        let newInitialValues = { ...initialValues }

        if (existingShareholders.length > 0) {
          const shareholder = existingShareholders[0]
          console.log('Found existing shareholder data:', shareholder)

          const address =
            shareholder.addresses && shareholder.addresses.length > 0
              ? shareholder.addresses[0]
              : null

          // Extract birth date from RFC if available
          let birthDate = shareholder.birthDate
            ? shareholder.birthDate.split('T')[0]
            : ''

          // If we have RFC but no birth date, or if RFC and birth date don't match, prioritize RFC
          if (shareholder.rfc) {
            const rfcBirthDate = extractBirthDateFromRFC(shareholder.rfc)
            if (rfcBirthDate) {
              // If no existing birth date or dates don't match, use RFC date
              if (!birthDate || birthDate !== rfcBirthDate) {
                birthDate = rfcBirthDate
                console.log(
                  '📅 Using birth date extracted from RFC:',
                  rfcBirthDate
                )
              }
            }
          }

          newInitialValues = {
            firstName: shareholder.firstName || '',
            lastName: shareholder.paternalLastName || '',
            motherLastName: shareholder.maternalLastName || '',
            rfc: shareholder.rfc || '',
            birthDate: birthDate,
            nationality:
              shareholder.nationality === 'Mexicana'
                ? 'mexico'
                : shareholder.nationality || '',
            phoneNumber: shareholder.phoneNumber || '',
            email: shareholder.email || '',
            street: address?.street || '',
            exteriorNumber: address?.exteriorNumber || '',
            interiorNumber: address?.interiorNumber || '',
            betweenStreets: address?.reference || '',
            postalCode: address?.postalCode || '',
            colony:
              typeof address?.neighborhood === 'string' &&
              address.neighborhood !== '[object Object]'
                ? address.neighborhood
                : '',
            city: address?.city || '',
            municipality: address?.municipality || '',
            state: address?.state || '',
            stateAbbreviation: address?.stateAbbreviation || '',
            ineFront: null,
            ineBack: null,
          }

          // Check if shareholder has documents (images already uploaded)
          if (shareholder.documents && shareholder.documents.length > 0) {
            console.log('Found existing documents:', shareholder.documents)
            const ineFrontDoc = shareholder.documents.find(
              (doc: any) => doc.type === 'INE_FRONT'
            )
            const ineBackDoc = shareholder.documents.find(
              (doc: any) => doc.type === 'INE_BACK'
            )

            if (ineFrontDoc) {
              setPreviews(prev => ({
                ...prev,
                ineFront: ineFrontDoc.url || 'existing',
              }))
            }
            if (ineBackDoc) {
              setPreviews(prev => ({
                ...prev,
                ineBack: ineBackDoc.url || 'existing',
              }))
            }
          }

          // If postal code exists, populate the postal code data and colonies
          if (address?.postalCode) {
            try {
              const postalData = await postalCodeService.searchByPostalCode(
                address.postalCode
              )
              if (postalData) {
                setPostalCodeData(postalData)
                setColonies(
                  postalData.colonias?.map((col: string) => ({
                    value: col,
                    label: col,
                  })) || []
                )
              }
            } catch (error) {
              console.error('Error loading postal code data:', error)
            }
          }

          toast.success('Datos existentes cargados')
        }

        setInitialValues(newInitialValues)
      } catch (error) {
        console.error('Error loading existing data:', error)
      } finally {
        setIsLoadingData(false)
      }
    }

    loadExistingData()
  }, [])

  // Handle postal code lookup
  const handlePostalCodeChange = async (value: string, setFieldValue: any) => {
    setFieldValue('postalCode', value)

    // Clear previous data when changing postal code
    if (value.length !== 5) {
      setPostalCodeData(null)
      setColonies([])
      setFieldValue('colony', '')
      setFieldValue('city', '')
      setFieldValue('municipality', '')
      setFieldValue('state', '')
      setFieldValue('stateAbbreviation', '')
      return
    }

    // Validate postal code format
    if (!/^\d{5}$/.test(value)) {
      return
    }

    try {
      console.log('Looking up postal code:', value)
      const data = await postalCodeService.searchByPostalCode(value)
      console.log('Postal code data:', data)

      if (data) {
        setPostalCodeData(data)

        // Create colonies options
        const coloniesOptions =
          data.colonias?.map((colony: string) => ({
            value: colony,
            label: colony,
          })) || []

        setColonies(coloniesOptions)

        // Auto-fill city, municipality, state, and state abbreviation
        setFieldValue('city', data.ciudad || '')
        setFieldValue('municipality', data.municipio || '')
        setFieldValue('state', data.estado || '')
        setFieldValue('stateAbbreviation', data.abreviatura || '')

        toast.success('Código postal encontrado')
      } else {
        toast.error('Código postal no encontrado')
      }
    } catch (error) {
      console.error('Error looking up postal code:', error)
      toast.error('Error al buscar código postal')
    }
  }

  const handleFileChange =
    (field: 'ineFront' | 'ineBack') => (file: File | null) => {
      if (file) {
        const reader = new FileReader()
        reader.onloadend = () => {
          setPreviews(prev => ({ ...prev, [field]: reader.result as string }))
        }
        reader.readAsDataURL(file)
      } else {
        setPreviews(prev => ({ ...prev, [field]: null }))
      }
    }

  const handleFormSubmit = async (values: ShareholderFormValues) => {
    try {
      // Trim all string values
      const trimmedValues = {
        ...values,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        motherLastName: values.motherLastName.trim(),
        rfc: values.rfc.trim(),
        birthDate: values.birthDate.trim(),
        nationality: values.nationality.trim(),
        phoneNumber: values.phoneNumber.trim(),
        email: values.email.trim(),
        street: values.street.trim(),
        exteriorNumber: values.exteriorNumber.trim(),
        interiorNumber: values.interiorNumber.trim(),
        betweenStreets: values.betweenStreets.trim(),
        postalCode: values.postalCode.trim(),
        colony: values.colony.trim(),
        city: values.city.trim(),
        municipality: values.municipality.trim(),
        state: values.state.trim(),
        stateAbbreviation: values.stateAbbreviation.trim(),
        // Files remain as they are
        ineFront: values.ineFront,
        ineBack: values.ineBack,
      }

      // Validate files - only required if not already uploaded
      const hasExistingIneFront =
        previews.ineFront &&
        (previews.ineFront === 'existing' ||
          previews.ineFront.startsWith('http') ||
          previews.ineFront.startsWith('data:'))
      const hasExistingIneBack =
        previews.ineBack &&
        (previews.ineBack === 'existing' ||
          previews.ineBack.startsWith('http') ||
          previews.ineBack.startsWith('data:'))

      if (!trimmedValues.ineFront && !hasExistingIneFront) {
        toast.error('La imagen del frente del INE es requerida')
        return
      }
      if (!trimmedValues.ineBack && !hasExistingIneBack) {
        toast.error('La imagen del reverso del INE es requerida')
        return
      }

      const files = {
        ineFront: trimmedValues.ineFront,
        ineBack: trimmedValues.ineBack,
      }

      const creditAppId = await authService.getCurrentCreditApplicationId()
      const savedShareholder = await majorityShareholderService.createComplete(
        trimmedValues,
        files,
        creditAppId
      )

      console.log('Shareholder saved successfully:', savedShareholder)
      toast.success('Datos del accionista mayoritario guardados correctamente')

      // Actualizar estatus a 'Buro Accionista'
      try {
        const prospectoQuoteId = localStorage.getItem('prospectoQuoteId')
        const token = localStorage.getItem('auth_token')
        if (prospectoQuoteId && token) {
          await apiClient.patch(
            `/cotizador/mis-cotizaciones-prospecto/${prospectoQuoteId}/estatus`,
            { estatus: 'Buro Accionista' },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
          console.log('✅ Estatus "Buro Accionista" actualizado exitosamente')
        }
      } catch (estatusError) {
        console.error(
          '❌ Error actualizando estatus "Buro Accionista":',
          estatusError
        )
      }

      // Call onComplete if provided
      if (onComplete) {
        await onComplete({
          completedAt: new Date().toISOString(),
          data: savedShareholder,
        })
      }
    } catch (error: any) {
      console.error('Error saving shareholder data:', error)

      if (error.response?.data?.message) {
        toast.error(error.response.data.message)
      } else if (error.message) {
        toast.error(error.message)
      } else {
        toast.error('Error al guardar los datos del accionista mayoritario')
      }
    }
  }

  // Show loading screen while loading initial data
  if (isLoadingData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <svg
            className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="text-gray-600">Cargando datos...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-h3 font-bold text-gray-900 mb-4">
          Datos del Accionista Mayoritario
        </h1>
        <p className="text-gray-600 text-paragraph-2 max-w-3xl mx-auto">
          Para continuar, necesitamos los datos del Accionista Mayoritario y su
          identificación oficial. Esta información es esencial para validar la
          solicitud de arrendamiento.
        </p>
        <p className="text-gray-600 text-paragraph-2 max-w-3xl mx-auto mt-2">
          Recuerda, esta información se utilizará para realizar la consulta a
          Buró de Crédito.
        </p>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          setFieldValue,
          errors,
          touched,
          isSubmitting,
          handleBlur,
        }) => (
          <Form className="space-y-8">
            {/* Información personal */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-6">
                Información Personal
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Input
                  label="Nombre(s)*"
                  name="firstName"
                  placeholder="Escribe aquí"
                  value={values.firstName}
                  onChange={e =>
                    handleNameChange(e.target.value, 'firstName', setFieldValue)
                  }
                  onBlur={handleBlur}
                  error={
                    touched.firstName && errors.firstName
                      ? errors.firstName
                      : undefined
                  }
                />
                <Input
                  label="Apellido paterno*"
                  name="lastName"
                  placeholder="Escribe aquí"
                  value={values.lastName}
                  onChange={e =>
                    handleNameChange(e.target.value, 'lastName', setFieldValue)
                  }
                  onBlur={handleBlur}
                  error={
                    touched.lastName && errors.lastName
                      ? errors.lastName
                      : undefined
                  }
                />
                <Input
                  label="Apellido materno*"
                  name="motherLastName"
                  placeholder="Escribe aquí"
                  value={values.motherLastName}
                  onChange={e =>
                    handleNameChange(
                      e.target.value,
                      'motherLastName',
                      setFieldValue
                    )
                  }
                  onBlur={handleBlur}
                  error={
                    touched.motherLastName && errors.motherLastName
                      ? errors.motherLastName
                      : undefined
                  }
                />
                <Input
                  label="RFC*"
                  name="rfc"
                  placeholder="Escribe aquí"
                  value={values.rfc}
                  onChange={e => handleRfcChange(e.target.value, setFieldValue)}
                  onBlur={handleBlur}
                  error={touched.rfc && errors.rfc ? errors.rfc : undefined}
                  maxLength={13}
                  tooltip="La fecha de nacimiento se extraerá automáticamente del RFC. Debe corresponder a una persona mayor de 21 años." // Cambiado de "mayor de edad (18 años)" a "mayor de 21 años"
                />
                <Input
                  label="Fecha de nacimiento*"
                  name="birthDate"
                  type="date"
                  placeholder="dd/mm/aaaa"
                  value={values.birthDate}
                  onChange={e => setFieldValue('birthDate', e.target.value)}
                  onBlur={handleBlur}
                  error={
                    touched.birthDate && errors.birthDate
                      ? errors.birthDate
                      : undefined
                  }
                  disabled
                  tooltip="Debe coincidir con la fecha en el RFC y ser mayor de 21 años" // Cambiado de "mayor de edad" a "mayor de 21 años"
                />
                <Select
                  label="Nacionalidad*"
                  options={nationalityOptions}
                  placeholder="Selecciona"
                  value={values.nationality}
                  onValueChange={value => setFieldValue('nationality', value)}
                  error={
                    touched.nationality && errors.nationality
                      ? errors.nationality
                      : undefined
                  }
                />
                <Input
                  label="Número de celular*"
                  name="phoneNumber"
                  placeholder="Escribe aquí"
                  value={values.phoneNumber}
                  onChange={e =>
                    handlePhoneChange(e.target.value, setFieldValue)
                  }
                  onBlur={handleBlur}
                  error={
                    touched.phoneNumber && errors.phoneNumber
                      ? errors.phoneNumber
                      : undefined
                  }
                  maxLength={10}
                />
                <Input
                  label="Correo electrónico*"
                  name="email"
                  type="email"
                  placeholder="Escribe aquí"
                  value={values.email}
                  onChange={e => setFieldValue('email', e.target.value)}
                  onBlur={handleBlur}
                  error={
                    touched.email && errors.email ? errors.email : undefined
                  }
                />
              </div>
            </div>

            {/* Credencial INE */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Credencial INE
              </h3>
              <p className="text-gray-600 text-sm mb-6">
                En caso de ser extranjero, por favor sube la foto de tu FM4
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <FileUpload
                  label="Frente*"
                  accept="image/*,.pdf"
                  onFileChange={file => {
                    setFieldValue('ineFront', file)
                    handleFileChange('ineFront')(file)
                  }}
                  preview={previews.ineFront}
                />
                <FileUpload
                  label="Reverso*"
                  accept="image/*,.pdf"
                  onFileChange={file => {
                    setFieldValue('ineBack', file)
                    handleFileChange('ineBack')(file)
                  }}
                  preview={previews.ineBack}
                />
              </div>
            </div>

            {/* Domicilio */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Domicilio
              </h3>

              {!postalCodeData && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-700">
                    <svg
                      className="inline-block w-4 h-4 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Ingresa primero el código postal para autocompletar la
                    colonia, ciudad y estado
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Input
                  label="Calle*"
                  name="street"
                  placeholder="Escribe aquí"
                  value={values.street}
                  onChange={e => setFieldValue('street', e.target.value)}
                  onBlur={handleBlur}
                  error={
                    touched.street && errors.street ? errors.street : undefined
                  }
                />
                <Input
                  label="Número exterior*"
                  name="exteriorNumber"
                  placeholder="Escribe aquí"
                  value={values.exteriorNumber}
                  onChange={e =>
                    setFieldValue('exteriorNumber', e.target.value)
                  }
                  onBlur={handleBlur}
                  error={
                    touched.exteriorNumber && errors.exteriorNumber
                      ? errors.exteriorNumber
                      : undefined
                  }
                />
                <Input
                  label="Número interior"
                  name="interiorNumber"
                  placeholder="Escribe aquí"
                  value={values.interiorNumber}
                  onChange={e =>
                    setFieldValue('interiorNumber', e.target.value)
                  }
                  onBlur={handleBlur}
                  error={
                    touched.interiorNumber && errors.interiorNumber
                      ? errors.interiorNumber
                      : undefined
                  }
                />
                <Input
                  label="Entre calles"
                  name="betweenStreets"
                  placeholder="Escribe aquí"
                  value={values.betweenStreets}
                  onChange={e =>
                    setFieldValue('betweenStreets', e.target.value)
                  }
                  onBlur={handleBlur}
                  error={
                    touched.betweenStreets && errors.betweenStreets
                      ? errors.betweenStreets
                      : undefined
                  }
                />
                <Input
                  label="Código postal*"
                  name="postalCode"
                  placeholder="12345"
                  value={values.postalCode}
                  onChange={e =>
                    handlePostalCodeChange(e.target.value, setFieldValue)
                  }
                  onBlur={handleBlur}
                  maxLength={5}
                  error={
                    touched.postalCode && errors.postalCode
                      ? errors.postalCode
                      : undefined
                  }
                />

                {colonies.length > 0 ? (
                  <Select
                    label="Colonia*"
                    value={values.colony}
                    onValueChange={value => setFieldValue('colony', value)}
                    options={colonies}
                    placeholder="Selecciona una colonia"
                    error={
                      touched.colony && errors.colony
                        ? errors.colony
                        : undefined
                    }
                  />
                ) : (
                  <Input
                    label="Colonia*"
                    value={values.colony}
                    disabled
                    placeholder="Ingresa primero el código postal"
                    error={
                      touched.colony && errors.colony
                        ? errors.colony
                        : undefined
                    }
                  />
                )}

                <Input
                  label="Ciudad*"
                  value={values.city}
                  disabled={!postalCodeData}
                  placeholder={
                    !postalCodeData ? 'Se llenará automáticamente' : ''
                  }
                  onChange={e => setFieldValue('city', e.target.value)}
                  onBlur={handleBlur}
                  error={touched.city && errors.city ? errors.city : undefined}
                />

                <Input
                  label="Estado*"
                  value={values.state}
                  disabled={!postalCodeData}
                  placeholder={
                    !postalCodeData ? 'Se llenará automáticamente' : ''
                  }
                  onChange={e => setFieldValue('state', e.target.value)}
                  onBlur={handleBlur}
                  error={
                    touched.state && errors.state ? errors.state : undefined
                  }
                />
              </div>
            </div>

            {/* Campos obligatorios */}
            <div className="pt-4">
              <p className="text-sm text-gray-600">*Campos obligatorios</p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-center gap-4 pt-6">
              <Button
                className="px-12 py-3 min-w-[230px] sm:order-2"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Guardando...' : 'Continuar'}
              </Button>

              <div className="sm:order-1">
                <ContinueLaterModal />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default ShareholderDataStep
